import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  AVAILABILITY_CHANGE_EVENT,
  JOIN_PUBLIC_EVENT,
  type AvailabilityResponse,
} from '@kratispot/shared';
import { API_BASE, fetchAvailability } from '@/lib/api';
import { socketOrigin } from '@/hooks/use-venue-socket';

export type AvailabilityState =
  | { phase: 'loading' }
  | { phase: 'unknown' }
  | { phase: 'error' }
  | { phase: 'ready'; availability: AvailabilityResponse };

// The public page reads its own surface, and nothing else. §12
export function useAvailability(
  slug: string,
  date: string | null,
): { state: AvailabilityState; reload: () => void } {
  const [state, setState] = useState<AvailabilityState>({ phase: 'loading' });
  const [reads, setReads] = useState(0);
  const reload = useCallback(() => setReads((count) => count + 1), []);

  useEffect(() => {
    const abort = new AbortController();
    fetchAvailability(slug, date, abort.signal)
      .then((availability) => setState({ phase: 'ready', availability }))
      .catch((error: unknown) => {
        if (abort.signal.aborted) return;
        const status = (error as { status?: number }).status;
        setState({ phase: status === 404 ? 'unknown' : 'error' });
      });
    return () => abort.abort();
  }, [slug, date, reads]);

  return { state, reload };
}

// A nudge carrying nothing: the page re-reads its own surface for itself. §16
export function useAvailabilitySocket(slug: string, onChange: () => void): void {
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => {
    const socket: Socket = io(socketOrigin(API_BASE), { path: '/socket.io' });
    const join = (): void => {
      socket.emit(JOIN_PUBLIC_EVENT, { slug }, () => undefined);
    };
    socket.on('connect', join);
    socket.on(AVAILABILITY_CHANGE_EVENT, () => latest.current());
    return () => {
      socket.close();
    };
  }, [slug]);
}
